import { useQuery, useSuspenseInfiniteQuery } from '@tanstack/react-query'

import { getRequest } from 'src/lib/api-client'
import { queries } from 'src/lib/query-keys'
import type { ChatType } from 'src/shared/schemas/chats/chat-data'

interface ChannelIdsResponse {
  channelIds: string[]
  hasMore: boolean
  nextOffset?: number
}

const getChatChannels = async (query = '', types: ChatType[], limit = 25, offset = 0): Promise<ChannelIdsResponse> => {
  try {
    const formattedTypes = types.map((type) => type.replace(/-/g, '_'))
    const params = new URLSearchParams()
    formattedTypes.forEach((type) => params.append('types', type))
    params.append('query', query)
    params.append('limit', limit.toString())
    params.append('offset', offset.toString())
    const response = await getRequest<string[]>(`/feeds/channel-ids?${params.toString()}`)

    return {
      channelIds: !!response && response.length > 0 ? response : ['messaging:fake-never-happening-id'],
      hasMore: (response?.length || 0) === limit,
      nextOffset: offset + limit,
    }
  } catch (error) {
    console.error('Error fetching chat channels:', error)
    return {
      channelIds: [],
      hasMore: false,
    }
  }
}

export const useChatChannels = (query = '', types: ChatType[] = []) => {
  return useQuery({
    queryKey: queries.chats.channels(query, types).queryKey,
    queryFn: () => getChatChannels(query, types),
    retry: false,
    staleTime: 10,
  })
}

/**
 * React Query hook to fetch channel IDs with infinite pagination
 * @param query - Search query for channel IDs
 * @param types - Types of channels to return (partner_match, referral_share, network)
 * @param limit - Number of results to return per page
 */
export const useChatSearch = (query = '', types: ChatType[] = [], limit = 25) => {
  return useSuspenseInfiniteQuery({
    queryKey: ['channel-ids', query, types, limit],
    queryFn: async ({ pageParam = 0 }) => {
      return await getChatChannels(query, types, limit, pageParam as number)
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      // If there's more data, return the next offset
      if (lastPage.hasMore) {
        return lastPage.nextOffset
      }
      // Otherwise, return undefined to indicate there are no more pages
      return undefined
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  })
}
